import React, { useState, useEffect, useCallback } from "react";
import { useLicense } from "../../context/licenseContext";
import { useLocation_ } from "../../context/locationContext";
import { useToast } from "../../context/toastContext";
import apiClient from "../../api/apiClient";
import Button from "../../components/ui/Button";
import ConfirmModal from "../../components/ui/ConfirmModal";
import OrdersFilterBar from "./components/OrdersFilterBar";
import OrdersTable from "./components/OrdersTable";
import OrderDetailDrawer from "./components/OrderDetailDrawer";
import SendInvoiceModal from "../../components/SendInvoiceModal/SendInvoiceModal";
import ShipOrderModal from "../../components/ShipOrderModal/ShipOrderModal";
import Loading from "../../components/Loading/Loading";
import "./Orders.css";

const TAB_STATUS_MAP = {
    all: "all",
    needs_attention: "all",
    pending: "requested,invoice_sent,paid,shipped,handoff_partially_confirmed",
    completed: "completed",
    cancelled: "denied,cancelled",
}; 
 
const PAGE_SIZE = 20; 

const Orders = ({ setHeaderTitle }) => {
    const { currentLicense, licensesLoaded } = useLicense();
    const { currentLocation } = useLocation_();
    const toast = useToast();

    const [orders, setOrders] = useState([]);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1); 
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedOrder, setSelectedOrder] = useState(null);

    const [actionOrder, setActionOrder] = useState(null);
    const [showInvoiceModal, setShowInvoiceModal] = useState(false);
    const [showShipModal, setShowShipModal] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);

    const [activeTab, setActiveTab] = useState("all");
    const [roleFilter, setRoleFilter] = useState("all");
    const [counts, setCounts] = useState({
        all: 0,
        needs_attention: 0,
        pending: 0,
        completed: 0,
        cancelled: 0,
    });

    useEffect(() => {
        setHeaderTitle("Orders");
    }, [setHeaderTitle]);

    const fetchOrders = useCallback(async () => {
        if (!licensesLoaded || !currentLicense?.licenseNo) {
            setOrders([]);
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const params = new URLSearchParams();
            params.set("licenseNo", currentLicense.licenseNo);
            params.set("status", TAB_STATUS_MAP[activeTab] || "all");
            params.set("type", "marketplace");
            params.set("role", roleFilter);
            params.set("page", currentPage);
            params.set("limit", PAGE_SIZE);

            if (activeTab === "needs_attention") {
                params.set("needs_action", "true");
            } else if (activeTab === "pending") {
                params.set("needs_action", "false");
            }

            const res = await apiClient.get(`/orders?${params.toString()}`);
            const data = res.data;
            setOrders(data.orders || []);
            setTotal(data.total || 0);
            setTotalPages(data.totalPages || 1);
        } catch (err) {
            console.error("Failed to fetch orders:", err);
            setError("Failed to load orders. Please try again.");
            setOrders([]);
        } finally { 
            setIsLoading(false);
        }
    }, [currentLicense?.licenseNo, licensesLoaded, activeTab, roleFilter, currentPage]);

    const fetchCounts = useCallback(async () => {
        if (!currentLicense?.licenseNo) return;

        try {
            const baseParams = `licenseNo=${currentLicense.licenseNo}&type=marketplace&role=${roleFilter}`;

            const [allRes, needsRes, pendingRes, completedRes, cancelledRes] = await Promise.all([
                apiClient.get(`/orders?${baseParams}&status=all&page=1&limit=1`),
                apiClient.get(`/orders?${baseParams}&status=all&needs_action=true&page=1&limit=1`),
                apiClient.get(`/orders?${baseParams}&status=requested,invoice_sent,paid,shipped,handoff_partially_confirmed&needs_action=false&page=1&limit=1`),
                apiClient.get(`/orders?${baseParams}&status=completed&page=1&limit=1`),
                apiClient.get(`/orders?${baseParams}&status=denied,cancelled&page=1&limit=1`),
            ]);

            setCounts({
                all: allRes.data.total || 0,
                needs_attention: needsRes.data.total || 0,
                pending: pendingRes.data.total || 0,
                completed: completedRes.data.total || 0,
                cancelled: cancelledRes.data.total || 0,
            });
        } catch (err) {
            console.error("[Orders] fetchCounts failed:", err);
        }
    }, [currentLicense?.licenseNo, roleFilter]);

    useEffect(() => {
        fetchOrders();
    }, [fetchOrders]);

    useEffect(() => {
        fetchCounts();
    }, [fetchCounts]);

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setCurrentPage(1);
    };

    const handleRoleChange = (role) => {
        setRoleFilter(role);
        setCurrentPage(1);
    };

    const handleSelectOrder = (order) => {
        setSelectedOrder(order);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const fetchOrderDetail = useCallback(async (orderId) => {
        const res = await apiClient.get(
            `/orders/${orderId}?licenseNo=${encodeURIComponent(currentLicense.licenseNo)}`
        );
        return res.data.order;
    }, [currentLicense?.licenseNo]);

    const handleTableAction = useCallback(async (actionType, order) => {
        switch (actionType) {
            case "send-invoice":
                try {
                    setActionLoading(true);
                    const fullOrder = await fetchOrderDetail(order.id);
                    setActionOrder(fullOrder);
                    setShowInvoiceModal(true);
                } catch (err) {
                    toast.error(err.response?.data?.error || "Failed to load order details.");
                } finally {
                    setActionLoading(false);
                }
                return;
            case "ship":
                try {
                    setActionLoading(true);
                    const fullOrder = await fetchOrderDetail(order.id);
                    setActionOrder(fullOrder);
                    setShowShipModal(true);
                } catch (err) {
                    toast.error(err.response?.data?.error || "Failed to load order details.");
                } finally {
                    setActionLoading(false);
                }
                return;
            case "confirm-payment":
                setActionOrder(order);
                setConfirmAction({
                    type: "confirm-payment",
                    title: "Confirm Payment",
                    message: "Confirm that you have received payment for this order?",
                    variant: "primary",
                });
                return;
            case "notify-payment":
                setActionOrder(order);
                setConfirmAction({
                    type: "notify-payment",
                    title: "Notify Payment",
                    message: "Let the seller know you have sent payment? This does not confirm the payment — the seller will verify and confirm.",
                    variant: "primary",
                });
                return;
            default:
                setSelectedOrder(order);
        }
    }, [fetchOrderDetail, toast]);

    const handleConfirmAction = useCallback(async () => {
        if (!confirmAction || !actionOrder) return;
        setActionLoading(true);
        try {
            await apiClient.patch(
                `/orders/${actionOrder.id}/${confirmAction.type}`,
                { licenseNo: currentLicense.licenseNo }
            );
            toast.success(
                confirmAction.type === "confirm-payment"
                    ? "Payment confirmed"
                    : "Payment notification sent to seller"
            );
            fetchOrders();
            fetchCounts();
        } catch (err) {
            toast.error(err.response?.data?.error || "Action failed.");
        } finally {
            setActionLoading(false);
            setConfirmAction(null);
            setActionOrder(null);
        }
    }, [confirmAction, actionOrder, currentLicense, toast, fetchOrders, fetchCounts]);

    const handleSendInvoice = useCallback(async (payload) => {
        if (!actionOrder) return;
        setActionLoading(true);
        try {
            await apiClient.patch(`/orders/${actionOrder.id}/send-invoice`, {
                licenseNo: currentLicense.licenseNo,
                ...payload,
            });
            toast.success("Invoice sent successfully");
            setShowInvoiceModal(false);
            setActionOrder(null);
            fetchOrders();
            fetchCounts();
        } catch (err) {
            toast.error(err.response?.data?.error || "Failed to send invoice.");
        } finally {
            setActionLoading(false);
        }
    }, [actionOrder, currentLicense, toast, fetchOrders, fetchCounts]);

    const handleShipOrder = useCallback(async (payload) => {
        if (!actionOrder) return;
        setActionLoading(true);
        try {
            await apiClient.patch(`/orders/${actionOrder.id}/ship`, {
                licenseNo: currentLicense.licenseNo,
                ...payload,
            });
            toast.success(
                payload.fulfillment_method === "carrier_shipping"
                    ? "Order marked as shipped"
                    : "Fulfillment method confirmed"
            );
            setShowShipModal(false);
            setActionOrder(null);
            fetchOrders();
            fetchCounts();
        } catch (err) {
            toast.error(err.response?.data?.error || "Failed to ship order.");
        } finally {
            setActionLoading(false);
        }
    }, [actionOrder, currentLicense, toast, fetchOrders, fetchCounts]);

    if (!licensesLoaded) {
        return (
            <div className="orders-page">
                <Loading />
            </div>
        );
    }

    if (!currentLicense?.licenseNo) {
        return (
            <div className="orders-page">
                <div className="orders-empty">
                    <div className="orders-empty__icon">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M16 16l3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z" />
                            <path d="m2 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z" />
                            <path d="M7 21h10" />
                            <path d="M12 3v18" />
                            <path d="M3 7h2c2 0 5-1 7-2 2 1 5 2 7 2h2" />
                        </svg>
                    </div>
                    <h3 className="orders-empty__title">Select a License</h3>
                    <p className="orders-empty__text">Choose a license from the header to view your orders.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="orders-page">
            <OrdersFilterBar
                activeTab={activeTab}
                onTabChange={handleTabChange}
                roleFilter={roleFilter}
                onRoleChange={handleRoleChange}
                counts={counts}
            />

            <div className="orders-content" data-tutorial="orders-table-container">
                {isLoading && (
                    <div className="orders-loading">
                        <Loading />
                    </div>
                )}

                {!isLoading && error && (
                    <div className="orders-error">
                        <div className="orders-error__icon">
                            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10" />
                                <line x1="12" y1="8" x2="12" y2="12" />
                                <line x1="12" y1="16" x2="12.01" y2="16" />
                            </svg>
                        </div>
                        <p className="orders-error__text">{error}</p>
                        <Button variant="primary" size="sm" onClick={fetchOrders}>
                            Retry
                        </Button>
                    </div>
                )}

                {!isLoading && !error && orders.length === 0 && (
                    <div className="orders-empty">
                        <div className="orders-empty__icon">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
                                <path d="m3.3 7 8.7 5 8.7-5" />
                                <path d="M12 22V12" />
                            </svg>
                        </div>
                        <h3 className="orders-empty__title">No orders yet</h3>
                        <p className="orders-empty__text">
                            Visit the DYC Marketplace to get started.
                        </p>
                    </div>
                )}

                {!isLoading && !error && orders.length > 0 && (
                    <OrdersTable
                        orders={orders}
                        onSelectOrder={handleSelectOrder}
                        onAction={handleTableAction}
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                        currentLicenseNo={currentLicense.licenseNo}
                    />
                )}
            </div>

            {selectedOrder && (
                <OrderDetailDrawer
                    order={selectedOrder}
                    onClose={() => setSelectedOrder(null)}
                    onOrderUpdated={() => {
                        fetchOrders();
                        fetchCounts();
                        setSelectedOrder(null);
                    }}
                />
            )}

            {showInvoiceModal && actionOrder && (
                <SendInvoiceModal
                    order={actionOrder}
                    onSend={handleSendInvoice}
                    onCancel={() => {
                        setShowInvoiceModal(false);
                        setActionOrder(null);
                    }}
                />
            )}

            {showShipModal && actionOrder && (
                <ShipOrderModal
                    order={actionOrder}
                    onShip={handleShipOrder}
                    onCancel={() => {
                        setShowShipModal(false);
                        setActionOrder(null);
                    }}
                />
            )}

            {confirmAction && (
                <ConfirmModal
                    title={confirmAction.title}
                    message={confirmAction.message}
                    confirmLabel="Confirm"
                    variant={confirmAction.variant || "danger"}
                    onConfirm={handleConfirmAction}
                    onCancel={() => {
                        setConfirmAction(null);
                        setActionOrder(null);
                    }}
                />
            )}
        </div>
    );
};

export default Orders;
